import {
  computed,
  getCurrentInstance,
  inject,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  unref,
} from 'vue'
import { debugWarn, isUndefined } from '@element-plus/utils'
import { carouselContextKey } from './constants'

import type { CarouselItemProps } from './carousel-item'

export const useCarouselItem = (
  props: CarouselItemProps,
  componentName: string
) => {
  const carouselContext = inject(carouselContextKey)!
  // instance
  const instance = getCurrentInstance()!
  if (!carouselContext) {
    debugWarn(
      componentName,
      'usage: <el-carousel></el-carousel-item></el-carousel>'
    )
  }

  if (!instance) {
    debugWarn(
      componentName,
      'compositional hook can only be invoked inside setups'
    )
  }

  const ITEM_WIDTH = 1
  const ITEM_SPACE = 0
  const CARD_SCALE = 0.83
  const CARD_WIDTH = 0.5

  const carouselItemRef = ref<HTMLElement>()
  const hover = ref(false)
  const translate = ref(0)
  const scale = ref(1)
  const active = ref(false)
  const ready = ref(false)
  const inStage = ref(false)
  const animating = ref(false)

  // computed
  const { isCardType, isVertical, customSize } = carouselContext

  const _itemWidth = computed(() => customSize.value[0] || ITEM_WIDTH)
  const _itemSpace = computed(() => customSize.value[1] || ITEM_SPACE)
  const _cardWidth = computed(() => customSize.value[2] || CARD_WIDTH)
  const _cardScale = computed(() => customSize.value[3] || CARD_SCALE)

  // methods

  function processIndex(index: number, activeIndex: number, length: number) {
    const lastItemIndex = length - 1
    const prevItemIndex = activeIndex - 1
    const nextItemIndex = activeIndex + 1
    const halfItemIndex = length / 2

    if (activeIndex === 0 && index === lastItemIndex) {
      return -1
    } else if (activeIndex === lastItemIndex && index === 0) {
      return length
    } else if (index < prevItemIndex && activeIndex - index >= halfItemIndex) {
      return length + 1
    } else if (index > nextItemIndex && index - activeIndex >= halfItemIndex) {
      return -2
    }
    return index
  }

  function calcCardTranslate(index: number, activeIndex: number) {
    const parentWidth = unref(isVertical)
      ? carouselContext.root.value?.offsetHeight || 0
      : carouselContext.root.value?.offsetWidth || 0

    // inStage 三个卡片间距符合等差 d = pw * (1 - cw * s) / 2, a1 = pw * (1 - cw) / 2
    if (inStage.value) {
      return (
        (parentWidth *
          ((1 - unref(_cardWidth) * unref(_cardScale)) * (index - activeIndex) +
            (1 - unref(_cardWidth)))) /
        2
      )
    } else if (index < activeIndex) {
      return (-(1 + unref(_cardScale)) * parentWidth * unref(_cardWidth)) / 2
    } else {
      return (
        ((2 - unref(_cardWidth) * (1 - unref(_cardScale))) * parentWidth) / 2
      )
    }
  }

  function calcTranslate(
    index: number,
    activeIndex: number,
    isVertical: boolean
  ) {
    const rootEl = carouselContext.root.value
    if (!rootEl) return 0

    const distance =
      (isVertical ? rootEl.offsetHeight : rootEl.offsetWidth) || 0
    return (
      (distance * unref(_itemWidth) + unref(_itemSpace)) * (index - activeIndex)
    )
  }

  const translateItem = (
    index: number,
    activeIndex: number,
    oldIndex?: number
  ) => {
    const _isCardType = unref(isCardType)
    const carouselItemLength = carouselContext.items.value.length ?? Number.NaN

    const isActive = index === activeIndex
    if (!_isCardType && !isUndefined(oldIndex)) {
      animating.value = isActive || index === oldIndex
    }

    if (!isActive && carouselItemLength > 2 && carouselContext.loop) {
      index = processIndex(index, activeIndex, carouselItemLength)
    }

    const _isVertical = unref(isVertical)
    active.value = isActive

    if (_isCardType) {
      inStage.value = Math.round(Math.abs(index - activeIndex)) <= 1
      translate.value = calcCardTranslate(index, activeIndex)
      scale.value = unref(active) ? 1 : unref(_cardScale) || CARD_SCALE
    } else {
      translate.value = calcTranslate(index, activeIndex, _isVertical)
    }

    ready.value = true

    if (isActive && carouselItemRef.value) {
      carouselContext.setContainerHeight(carouselItemRef.value.offsetHeight)
    }
  }

  function handleItemClick() {
    if (carouselContext && (unref(isCardType) || unref(_itemWidth) < 1)) {
      const index = carouselContext.items.value.findIndex(
        ({ uid }) => uid === instance.uid
      )
      carouselContext.setActiveItem(index)
    }
  }

  // lifecycle
  onMounted(() => {
    carouselContext.addItem({
      props,
      states: reactive({
        hover,
        translate,
        scale,
        active,
        size: unref(isCardType) ? _cardWidth : _itemWidth,
        ready,
        inStage,
        animating,
      }),
      uid: instance.uid,
      translateItem,
    })
  })

  onUnmounted(() => {
    carouselContext.removeItem(instance.uid)
  })

  return {
    carouselItemRef,
    active,
    animating,
    hover,
    inStage,
    isVertical,
    translate,
    isCardType,
    scale,
    size: unref(isCardType) ? _cardWidth : _itemWidth,
    ready,
    handleItemClick,
  }
}
